/**
 * Abstract error messages for secure, user-friendly error handling
 *
 * All messages are intentionally abstract - no technical details,
 * implementation specifics, or internal system information exposed.
 *
 * Security requirements from CONTEXT.md:
 * - No database schema names
 * - No internal API paths
 * - No stack traces
 * - No error codes
 *
 * @module errors/messages
 */

/**
 * User-friendly error messages
 *
 * All messages are abstract and provide no technical details.
 * This prevents information leakage while maintaining good UX.
 */
export const ErrorMessages = {
  // Generic errors
  UNKNOWN: "An unexpected error occurred. Please try again.",
  NETWORK: "Unable to connect. Please check your internet connection.",
  TIMEOUT: "Request timed out. Please try again.",

  // Authentication errors
  UNAUTHORIZED: "Please sign in to continue.",
  FORBIDDEN: "You do not have permission to perform this action.",
  SESSION_EXPIRED: "Your session has expired. Please sign in again.",

  // Data errors
  NOT_FOUND: "The requested information could not be found.",
  CONFLICT: "This record already exists or has been modified.",
  VALIDATION: "Please check your input and try again.",

  // Storage errors
  UPLOAD_FAILED: "Failed to upload file. Please try again.",
  FILE_TOO_LARGE: "The file is too large. Please choose a smaller file.",
  INVALID_FILE_TYPE: "This file type is not supported.",

  // Database errors
  SAVE_FAILED: "Failed to save changes. Please try again.",
  DELETE_FAILED: "Failed to delete. Please try again.",
  FETCH_FAILED: "Failed to load data. Please refresh the page.",
} as const;

/**
 * Type for accessing error message keys
 */
export type ErrorMessageKey = keyof typeof ErrorMessages;

/**
 * Auth-related error keywords for pattern matching.
 *
 * Shared by user-message mapping and API retry/auth detection.
 */
export const AUTH_ERROR_KEYWORDS = [
  "JWT",
  "unauthorized",
  "401",
  "403",
  "forbidden",
  "token",
  "session",
  "auth",
] as const;

/**
 * Network-related error keywords for pattern matching.
 *
 * Shared by user-message mapping and API retry detection.
 */
export const NETWORK_ERROR_KEYWORDS = [
  "network",
  "fetch",
  "ECONNREFUSED",
  "ENOTFOUND",
  "offline",
] as const;

/**
 * Timeout-related error keywords for pattern matching.
 */
export const TIMEOUT_ERROR_KEYWORDS = [
  "timeout",
  "timed out",
  "ETIMEDOUT",
] as const;

/**
 * Extract an error's technical message for store state and rethrow paths.
 *
 * This intentionally preserves existing store behavior: Error instances and
 * API-shaped objects with string messages return that message; all other
 * values return a stable fallback.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
  }

  return "Unknown error";
}

/**
 * Get a user-friendly error message from an error object
 *
 * Maps technical errors to safe, abstract messages. No implementation
 * details are exposed to users (security requirement).
 *
 * @param error - Error object (can be Error, string, or unknown)
 * @returns User-friendly error message string
 */
export function getUserMessage(error: unknown): string {
  // Extract error message safely
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorLower = errorMessage.toLowerCase();

  // Check for authentication errors
  if (
    AUTH_ERROR_KEYWORDS.some((keyword) =>
      errorLower.includes(keyword.toLowerCase()),
    )
  ) {
    return ErrorMessages.SESSION_EXPIRED;
  }

  // Check for network errors
  if (
    NETWORK_ERROR_KEYWORDS.some((keyword) =>
      errorLower.includes(keyword.toLowerCase()),
    )
  ) {
    return ErrorMessages.NETWORK;
  }

  // Check for timeout errors
  if (
    TIMEOUT_ERROR_KEYWORDS.some((keyword) =>
      errorLower.includes(keyword.toLowerCase()),
    )
  ) {
    return ErrorMessages.TIMEOUT;
  }

  // Default fallback - safe, abstract message
  return ErrorMessages.UNKNOWN;
}

/**
 * Log error details for development
 *
 * Logs full error details with context prefix for debugging.
 * These logs are for development only and never exposed to users.
 *
 * @param context - Context where error occurred (e.g., 'API', 'Component')
 * @param error - Error object to log
 */
export function logError(context: string, error: unknown): void {
  // Only log in development
  if (import.meta.env.DEV) {
    const prefix = `[${context}]`;

    if (error instanceof Error) {
      console.error(prefix, error.message);
      console.error(prefix, "Stack:", error.stack);
    } else {
      console.error(prefix, "Unknown error:", error);
    }
  }
}
